import React, { useState, useEffect } from "react";
import axios from 'axios';
import { useApplicationPackages, ApplicationPackage } from "../../hooks/useApplicationPackages";
import CommonModal from "../../components/common/CommonModal";
import {
  TextField,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  CircularProgress,
  Autocomplete,
  SelectChangeEvent,
  Switch,
  Slider,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Language as WebsiteIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import { SchoolData, SchoolAdmin, useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { getAuthToken, saveSchoolFormDraft } from "utils/storage";
import {
  capitalizeWords,
} from "../../utils/validation";
import UserFormModal from "./UserFormModal";
import { useUsers } from "../../hooks/useUsers";
import { UserData } from "../../services/userService";
import { useError } from "../../contexts/ErrorContext";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';

// Fix the default Leaflet marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const parseTimeTo12Hour = (timeVal: any) => {
  if (!timeVal) return { hour: "09", minute: "00", period: "AM" };

  let hour = 9;
  let minute = "00";

  if (Array.isArray(timeVal)) {
    const [h = 9, m = 0] = timeVal;
    hour = h;
    minute = String(m).padStart(2, "0");
  } else {
    const timeStr = String(timeVal).trim();
    const parts = timeStr.split(":");
    hour = parseInt(parts[0], 10);
    minute = parts[1] ? parts[1].substring(0, 2) : "00";
  }

  if (isNaN(hour)) return { hour: "09", minute: "00", period: "AM" };
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12;
  return { hour: String(hour).padStart(2, "0"), minute, period };
};

const formatTo24Hour = (hourStr: string, minuteStr: string, period: string): string => {
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr.padStart(2, "0");
  if (period === "PM" && hour < 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${minute}:00`;
};

const hoursOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const minutesOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));


interface SchoolFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: SchoolData;
  onSubmit: (
    formData: SchoolData,
    imageFile?: File,
    schoolImageFile?: File
  ) => void;
  isEdit: boolean;
  schoolAdmins: SchoolAdmin[];
  refetchSchoolAdmins?: () => void;
}

const SchoolFormModal: React.FC<SchoolFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolAdmins,
  refetchSchoolAdmins,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { showError } = useError();

  // Get the hooks from useSchools
  const { useSchoolLogoInfo, useSchoolImageInfo, createSchoolMutation, updateSchoolMutation } = useSchools();

  // User management hooks
  const { createUserMutation } = useUsers();
  const { packagesQuery } = useApplicationPackages();
  const [userModalOpen, setUserModalOpen] = useState(false);

  const packageOptions = (packagesQuery.data || []).filter((pkg) => pkg.active !== false);

  const getPackageLabel = (pkg: ApplicationPackage) => {
    const name = pkg.name || "";
    if (pkg.price == null) return name;
    const priceLabel =
      Number(pkg.price) === 0 ? t("free") : `₹${Number(pkg.price).toLocaleString("en-IN")}`;
    return `${name} — ${priceLabel}`;
  };

  // Add step state
  const [currentStep, setCurrentStep] = useState(1);

  // Get logo and image info when editing
  const { data: logoInfo } = useSchoolLogoInfo(
    isEdit && initialData?.id ? initialData.id : undefined
  );
  const { data: imageInfo } = useSchoolImageInfo(
    isEdit && initialData?.id ? initialData.id : undefined
  );

  // Derive loading states from mutations
  const isCreating = createSchoolMutation.isLoading;
  const isUpdating = updateSchoolMutation.isLoading;
  const isLoading = isCreating || isUpdating;

  // State for authenticated image URLs
  const [authenticatedLogoUrl, setAuthenticatedLogoUrl] = useState<string | null>(null);
  const [authenticatedSchoolImageUrl, setAuthenticatedSchoolImageUrl] = useState<string | null>(null);
  const [logoPreviewModal, setLogoPreviewModal] = useState<boolean>(false);
  const [schoolImagePreviewModal, setSchoolImagePreviewModal] = useState<boolean>(false);

  const initialFormState: SchoolData = {
    nameofSchool: "",
    schoolCode: "",
    diseCode: "",
    jsk_aeo: "",
    blockCode: "",
    blockName: "",
    districtCode: "",
    district: "",
    pinNo: "",
    state: "",
    schoolPrincipalName: "",
    principalDesignation: "",
    principalMobile: "",
    principalEmail: "",
    medium: [],
    schoolAdminIds: [],
    schoolLogo: "",
    schoolImage: "",
    active: true,
    adminId: user?.id || 0,
    emailServiceEnabled: false,
    whatsappServiceEnabled: false,
    websiteServiceEnabled: false,
    feePaymentService: false,
    customWebsiteUrl: "",
    createdAt: "",
    updatedAt: "",
    tenantId: 1, // Assuming a default tenantId
    tenant: {
      id: 1,
      schemaName: "public",
      tenantId: "public",
      name: "Public",
      contactEmail: "info@example.com",
      active: true,
    },
    domainName: "",
    teacherAttendanceEnabled: false,
    schoolStartTime: "09:00:00",
    schoolEndTime: "17:00:00",
    geoRadiusMetres: 200,
    geoLatitude: 20.5937,
    geoLongitude: 78.9629,
    applicationPackage: null,
  };

  const [formData, setFormData] = useState<SchoolData>(initialFormState);

  const selectedPackageValue =
    formData.applicationPackage?.id != null
      ? packageOptions.find((pkg) => pkg.id === formData.applicationPackage?.id) ??
        formData.applicationPackage
      : null;

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [schoolImageFile, setSchoolImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [schoolImagePreview, setSchoolImagePreview] = useState<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const [addressSearch, setAddressSearch] = useState("");
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [autoLocateAddress, setAutoLocateAddress] = useState(true);

  const handleAddressLookup = async (address: string) => {
    if (!address.trim()) return;
    try {
      setGeocodeLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setFormData((prev) => ({
          ...prev,
          geoLatitude: lat,
          geoLongitude: lon,
        }));
      } else {
        showError(t("address_not_found") || "Address not found. Please try a different search or click the map directly.");
      }
    } catch (err) {
      showError(t("failed_to_geocode") || "Failed to search address. Please try again.");
    } finally {
      setGeocodeLoading(false);
    }
  };

  const handleAutoFillAddress = () => {
    const parts = [
      formData.nameofSchool,
      formData.blockName,
      formData.district,
      formData.state,
      formData.pinNo
    ].filter(p => p && p.trim() !== "");

    if (parts.length === 0) {
      showError(t("no_school_details_to_fill") || "No school details found in Step 1 to auto-fill address.");
      return;
    }

    const fullAddr = parts.join(", ");
    setAddressSearch(fullAddr);
    handleAddressLookup(fullAddr);
  };

  useEffect(() => {
    if (!autoLocateAddress || !addressSearch || addressSearch.trim().length < 4) return;

    const delayDebounce = setTimeout(() => {
      handleAddressLookup(addressSearch);
    }, 1200);

    return () => clearTimeout(delayDebounce);
  }, [addressSearch, autoLocateAddress]);

  const currentAssignedAdminId =
    isEdit && formData.schoolAdminIds && formData.schoolAdminIds.length > 0
      ? formData.schoolAdminIds[0]
      : undefined;

  // Update the availableAdmins logic
  const availableAdmins = isEdit
    ? schoolAdmins.filter((admin) => {
      // When editing, show:
      // 1. The currently assigned admin for this school
      // 2. Admins not assigned to any school (schoolId is null)
      return admin.schoolId === null && admin.status !== false || admin.id === currentAssignedAdminId;
    })
    : schoolAdmins.filter((admin) => {
      // When creating new school, only show admins not assigned to any school
      return admin.schoolId === null && admin.status !== false;
    });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialFormState,
        ...initialData,
        adminId: user?.id || 0,
      });
    } else {
      setFormData({
        ...initialFormState,
        adminId: user?.id || 0,
      });
    }
    setErrors({});
    // Reset step when modal opens
    setCurrentStep(1);
    // Reset previews when modal opens
    setImagePreview(null);
    setSchoolImagePreview(null);
    setAuthenticatedLogoUrl(null);
    setAuthenticatedSchoolImageUrl(null);
    // Reset file states
    setImageFile(null);
    setSchoolImageFile(null);
  }, [initialData, user, open]);

  // Fetch and create authenticated logo URL
  useEffect(() => {
    if (logoInfo && isEdit && open && initialData?.id) {
      const constructedUrl = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;

      const token = getAuthToken();
      if (token) {
        const tenantId = axios.defaults.headers.common['X-Tenant-ID'];
        const tenantIdString = typeof tenantId === 'string' || typeof tenantId === 'number' ? String(tenantId) : '';
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': tenantIdString
          }
        })
          .then(response => {
            if (response.ok) {
              return response.blob();
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          })
          .then(blob => {
            const imageUrl = URL.createObjectURL(blob);
            setAuthenticatedLogoUrl(imageUrl);
          })
          .catch(error => {
            setAuthenticatedLogoUrl(null);
          });
      } else {
        setAuthenticatedLogoUrl(null);
      }
    }
  }, [logoInfo, isEdit, open, initialData?.id]);

  // Fetch and create authenticated school image URL
  useEffect(() => {
    if (imageInfo && isEdit && open && initialData?.id) {
      const constructedUrl = `${process.env.REACT_APP_API_URL}/schools/school-image/${imageInfo.fileName}`;

      const token = getAuthToken();
      if (token) {
        const tenantId = axios.defaults.headers.common['X-Tenant-ID'];
        const tenantIdString = typeof tenantId === 'string' || typeof tenantId === 'number' ? String(tenantId) : '';
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': tenantIdString
          }
        })
          .then(response => {
            if (response.ok) {
              return response.blob();
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          })
          .then(blob => {
            const imageUrl = URL.createObjectURL(blob);
            setAuthenticatedSchoolImageUrl(imageUrl);
          })
          .catch(error => {
            setAuthenticatedSchoolImageUrl(null);
          });
      } else {
        setAuthenticatedSchoolImageUrl(null);
      }
    }
  }, [imageInfo, isEdit, open, initialData?.id]);

  useEffect(() => {
    return () => {
      if (authenticatedLogoUrl) {
        URL.revokeObjectURL(authenticatedLogoUrl);
      }
      if (authenticatedSchoolImageUrl) {
        URL.revokeObjectURL(authenticatedSchoolImageUrl);
      }
    };
  }, [initialData?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    let newValue = value;

    // Field-specific formatting
    if (name === "nameofSchool") {
      newValue = capitalizeWords(value.replaceAll(/\s+/g, " ").trimStart());
    } else if (name === "schoolCode") {
      newValue = value.toUpperCase().replaceAll(/[^A-Z0-9\-/]/g, "");
    } else if (name === "principalMobile") {
      // Only allow digits and limit to 10 digits
      newValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "principalEmail") {
      // Convert email to lowercase
      newValue = value.toLowerCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error for this field if it exists (but don't validate while typing)
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };


  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file?.type.startsWith("image/")) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const handleSchoolImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file?.type.startsWith("image/")) {
      setSchoolImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setSchoolImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSchoolImageFile(null);
      setSchoolImagePreview(null);
    }
  };

  const handleRemoveLogo = () => {
    setImageFile(null);
    setImagePreview(null);
    setAuthenticatedLogoUrl(null);
  };

  const handleRemoveSchoolImage = () => {
    setSchoolImageFile(null);
    setSchoolImagePreview(null);
    setAuthenticatedSchoolImageUrl(null);
  };

  const handleSubmit = (formData: SchoolData, imageFile?: File, schoolImageFile?: File) => {
    const payload = {
      ...formData,
      schoolLogo: imageFile ? imageFile.name : "",
      schoolImage: schoolImageFile ? schoolImageFile.name : "",
      schoolAdminIds: formData.schoolAdminIds || [], // Will be array with single value
      adminId: user?.id || 0,
      emailServiceEnabled: formData.emailServiceEnabled,
      // Force these to false as they are hidden from UI
      whatsappServiceEnabled: false,
      websiteServiceEnabled: false,
      feePaymentService: formData.feePaymentService,
      applicationPackage: formData.applicationPackage?.id
        ? { id: formData.applicationPackage.id }
        : null,
    };

    // Save draft data before submitting (only for create, not edit)
    if (!isEdit) {
      saveSchoolFormDraft(formData);
    }

    onSubmit(payload, imageFile || undefined, schoolImageFile || undefined);
  };







  // Validation function to check and set errors for a step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (step === 1) {
      const mandatoryFields = [
        "nameofSchool",
        "schoolCode",
        "diseCode",
        "blockCode",
        "blockName",
        "districtCode",
        "district",
        "pinNo",
        "state"
      ];

      mandatoryFields.forEach((field) => {
        const value = (formData as Record<string, any>)[field];
        if (!value || (typeof value === 'string' && value.trim() === "")) {
          newErrors[field] = "Required field";
          isValid = false;
        }
      });
    } else if (step === 2) {
      const mandatoryFields = [
        "schoolPrincipalName",
        "principalDesignation",
        "principalMobile"
      ];

      mandatoryFields.forEach((field) => {
        const value = (formData as Record<string, any>)[field];
        if (!value || (typeof value === 'string' && value.trim() === "")) {
          newErrors[field] = "Required field";
          isValid = false;
        }
      });

      // Validate principal mobile number
      if (formData.principalMobile) {
        const mobile = formData.principalMobile.trim();
        // Check if it contains only digits
        if (!/^\d+$/.test(mobile)) {
          newErrors.principalMobile = "Mobile number should contain only digits";
          isValid = false;
        }
        // Check if it has exactly 10 digits
        else if (mobile.length !== 10) {
          newErrors.principalMobile = "Mobile number must contain exactly 10 digits";
          isValid = false;
        }
        // Check if it starts with 6, 7, 8, or 9
        else if (!/^[6789]/.test(mobile)) {
          newErrors.principalMobile = "Mobile number must start with 6, 7, 8, or 9";
          isValid = false;
        }
      }

      // Validate principal email
      if (formData.principalEmail) {
        const email = formData.principalEmail.trim();
        // Email validation regex pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          newErrors.principalEmail = "Please enter a valid email address";
          isValid = false;
        }
      }

      // Check school admin
      if (!formData.schoolAdminIds || formData.schoolAdminIds.length === 0) {
        newErrors.schoolAdminIds = "Required field";
        isValid = false;
      }
    } else if (step === 3) {
      // Check medium
      if (!formData.medium || formData.medium.length === 0) {
        newErrors.medium = "Required field";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Step validation functions (for button enable/disable)
  const isStep1Valid = () => {
    const mandatoryFields = [
      "nameofSchool",
      "schoolCode",
      "diseCode",
      "blockCode",
      "blockName",
      "districtCode",
      "district",
      "pinNo",
      "state"
    ];

    return mandatoryFields.every((field) => {
      const value = (formData as Record<string, any>)[field];
      return value !== undefined && value !== null && value !== "" && (typeof value !== 'string' || value.trim() !== "");
    });
  };

  const isStep2Valid = () => {
    const mandatoryFields = [
      "schoolPrincipalName",
      "principalDesignation",
      "principalMobile"
    ];

    const basicFieldsValid = mandatoryFields.every((field) => {
      const value = (formData as Record<string, any>)[field];
      return value !== undefined && value !== null && value !== "" && (typeof value !== 'string' || value.trim() !== "");
    });

    // Check if at least one school admin is selected
    const hasSchoolAdmin = formData.schoolAdminIds && formData.schoolAdminIds.length > 0;

    return basicFieldsValid && hasSchoolAdmin;
  };

  const isStep3Valid = () => {
    // Check if medium array has at least one selection
    const hasMediumSelection = formData.medium && formData.medium.length > 0;

    return hasMediumSelection;
  };

  const isStep4Valid = () => {
    // Services are optional, so this step is always valid
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep(1)) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 2) {
      if (validateStep(2)) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 3) {
      if (validateStep(3)) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 4) {
      if (isStep4Valid()) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 5) {
      handleSubmit(formData, imageFile || undefined, schoolImageFile || undefined);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 4) {
      setCurrentStep(3);
    } else if (currentStep === 5) {
      setCurrentStep(4);
    }
  };

  // Step content components
  const renderStep1 = () => (
    <Box sx={{ height: "260px", overflowY: "auto"}}>
      <Grid container spacing={2.5} sx={{mt: 1}}>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("school_name")}
            name="nameofSchool"
            value={formData.nameofSchool}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.nameofSchool}
            helperText={errors.nameofSchool ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("school_code")}
            name="schoolCode"
            value={formData.schoolCode}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.schoolCode}
            helperText={errors.schoolCode ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("dise_code")}
            name="diseCode"
            value={formData.diseCode}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.diseCode}
            helperText={errors.diseCode ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("jsk_aeo")}
            name="jsk_aeo"
            value={formData.jsk_aeo}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: '#f8f9fb' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("block_code")}
            name="blockCode"
            value={formData.blockCode}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.blockCode}
            helperText={errors.blockCode ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("block_name")}
            name="blockName"
            value={formData.blockName}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.blockName}
            helperText={errors.blockName ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("district_code")}
            name="districtCode"
            value={formData.districtCode}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.districtCode}
            helperText={errors.districtCode ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("district")}
            name="district"
            value={formData.district}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.district}
            helperText={errors.district ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("pin_code")}
            name="pinNo"
            value={formData.pinNo}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.pinNo}
            helperText={errors.pinNo ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("state")}
            name="state"
            value={formData.state}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.state}
            helperText={errors.state ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ height: "260px", overflowY: "auto"}}>
      <Grid container spacing={2.5} sx={{mt: 1}}>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("principal_name")}
            name="schoolPrincipalName"
            value={formData.schoolPrincipalName}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.schoolPrincipalName}
            helperText={errors.schoolPrincipalName ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("principal_designation")}
            name="principalDesignation"
            value={formData.principalDesignation}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.principalDesignation}
            helperText={errors.principalDesignation ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("principal_mobile")}
            name="principalMobile"
            value={formData.principalMobile}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.principalMobile}
            helperText={errors.principalMobile ? <span style={{ color: 'red' }}>{errors.principalMobile}</span> : ''}
            size="small"
            inputProps={{
              maxLength: 10,
              pattern: '[0-9]*',
              inputMode: 'numeric'
            }}
            sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("principal_email")}
            name="principalEmail"
            value={formData.principalEmail}
            onChange={handleChange}
            onBlur={(e) => {
              // Validate email
              const email = e.target.value.trim();
              if (email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                  setErrors((prev) => ({ ...prev, principalEmail: "Please enter a valid email address" }));
                } else {
                  setErrors((prev) => ({ ...prev, principalEmail: null }));
                }
              } else {
                setErrors((prev) => ({ ...prev, principalEmail: null }));
              }
            }}
            fullWidth
            size="small"
            type="email"
            error={!!errors.principalEmail}
            helperText={errors.principalEmail ? <span style={{ color: 'red' }}>{errors.principalEmail}</span> : ''}
            sx={{ backgroundColor: '#f8f9fb' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              select
              label={t("school_admin")}
              name="schoolAdminIds"
              value={formData.schoolAdminIds?.[0] || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  schoolAdminIds: value ? [Number(value)] : []
                }));
                // Clear error when value is selected
                if (value && errors.schoolAdminIds) {
                  setErrors(prev => ({ ...prev, schoolAdminIds: null }));
                }
              }}
              fullWidth
              required
              error={!!errors.schoolAdminIds}
              helperText={errors.schoolAdminIds ? <span style={{ color: 'red' }}>Required field</span> : (availableAdmins.length === 0 ? t("no_available_admins") : "")}
              size="small"
              sx={{ flex: 1, backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
            >
              {availableAdmins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.email}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setUserModalOpen(true)}
              sx={{
                mt: 0.5,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": {
                  borderColor: "#3092e3",
                  backgroundColor: "rgba(48, 146, 227, 0.08)",
                },
              }}
            >
              {t("add_user") || "Add User"}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("domain_name")}
            name="domainName"
            value={formData.domainName}
            onChange={handleChange}
            fullWidth
            error={!!errors.domainName}
            helperText={errors.domainName ? <span style={{ color: 'red' }}>Required field</span> : ''}
            size="small"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ height: "260px", overflowY: "auto"}}>
      <Grid container spacing={2.5} sx={{mt: 1}}>
        {/* School Logo and Image in the same row */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              height: '40px', // Match TextField height
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              bgcolor: 'background.default'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(imagePreview || authenticatedLogoUrl) ? (
                <Box
                  component="img"
                  src={imagePreview || authenticatedLogoUrl || undefined}
                  alt="Logo Preview"
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: 'contain',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    border: '1px solid #0168EA'
                  }}
                  onClick={() => setLogoPreviewModal(true)}
                />
              ) : (
                <ImageIcon fontSize="small" color="primary" />
              )}
              <Typography variant="body2">{t("school_logo")}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Delete Button - only show when image exists */}
              {(imagePreview || authenticatedLogoUrl) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRemoveLogo}
                  sx={{
                    minWidth: 'auto',
                    p: '4px 8px',
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      borderColor: '#d32f2f'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              )}
              {/* Upload Button */}
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ minWidth: 'auto', p: '4px 8px' }}
              >
                {(imagePreview || authenticatedLogoUrl) ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CloudUploadIcon fontSize="small" />
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              height: '40px', // Match TextField height
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              bgcolor: 'background.default'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(schoolImagePreview || authenticatedSchoolImageUrl) ? (
                <Box
                  component="img"
                  src={schoolImagePreview || authenticatedSchoolImageUrl || undefined}
                  alt="School Image Preview"
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: 'contain',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    border: '1px solid #0168EA'
                  }}
                  onClick={() => setSchoolImagePreviewModal(true)}
                />
              ) : (
                <ImageIcon fontSize="small" color="primary" />
              )}
              <Typography variant="body2">{t("school_image")}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Delete Button - only show when image exists */}
              {(schoolImagePreview || authenticatedSchoolImageUrl) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRemoveSchoolImage}
                  sx={{
                    minWidth: 'auto',
                    p: '4px 8px',
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      borderColor: '#d32f2f'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              )}
              {/* Upload Button */}
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ minWidth: 'auto', p: '4px 8px' }}
              >
                {(schoolImagePreview || authenticatedSchoolImageUrl) ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CloudUploadIcon fontSize="small" />
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleSchoolImageChange}
                />
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Medium and Package */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            freeSolo
            options={[
              "HINDI",
              "ENGLISH",
              "MARATHI",
              "KANNADA",
              "TELUGU",
              "TAMIL",
              "GUJARATI",
              "ODIA",
              "PUNJABI",
              "BENGALI",
              "URDU"
            ].filter(option => !formData.medium?.includes(option))} // Filter out already selected mediums
            value={formData.medium || []}
            onChange={(event, newValue) => {
              // Convert all values to uppercase
              const uppercaseValues = newValue.map(value =>
                typeof value === 'string' ? value.toUpperCase() : value
              );
              setFormData(prev => ({
                ...prev,
                medium: uppercaseValues
              }));
              // Clear error when value is selected
              if (uppercaseValues.length > 0 && errors.medium) {
                setErrors(prev => ({ ...prev, medium: null }));
              }
            }}
            onKeyDown={(event) => {
              // Prevent backspace from deselecting medium when input is empty
              if (event.key === 'Backspace') {
                const target = event.target as HTMLInputElement;
                if (!target.value || target.value.length === 0) {
                  event.stopPropagation();
                }
              }
            }}
            renderTags={() => null} // Don't show tags in the input field
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("medium")}
                placeholder={t("type_or_select_medium")}
                required
                error={!!errors.medium}
                helperText={errors.medium ? <span style={{ color: 'red' }}>Required field</span> : ''}
                size="small"
                sx={{ backgroundColor: '#f8f9fb', '& .MuiFormLabel-asterisk': {color: 'red'} }}
                onChange={(e) => {
                  // Convert to uppercase while typing
                  const inputElement = e.target as HTMLInputElement;
                  inputElement.value = inputElement.value.toUpperCase();
                  // Call the original onChange if it exists
                  if (params.inputProps?.onChange) {
                    params.inputProps.onChange(e as any);
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent backspace from deselecting medium when input is empty
                  if (e.key === 'Backspace' && !params.inputProps?.value) {
                    e.stopPropagation();
                  }
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={packageOptions}
            getOptionLabel={(option) => getPackageLabel(option)}
            value={selectedPackageValue}
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                applicationPackage: newValue,
              }));
            }}
            loading={packagesQuery.isLoading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("package")}
                placeholder={t("select_package")}
                size="small"
                sx={{ backgroundColor: "#f8f9fb" }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "nowrap",
              },
            }}
          />
        </Grid>

        {/* Selected Mediums Display */}
        {formData.medium && formData.medium.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("selected_mediums")}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.medium.map((medium) => (
                <Box
                  key={medium}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {String(t(medium.toLowerCase()) || medium).toUpperCase()}
                  <Button
                    size="small"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        medium: prev.medium?.filter(m => m !== medium) || []
                      }));
                    }}
                    sx={{
                      minWidth: 'auto',
                      px: 0.55,
                      mx: 0.15,
                      color: 'white',
                      border: '1px solid white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)'
                      }
                    }}
                  >
                    x
                  </Button>
                </Box>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const renderStep4 = () => (
    <Box sx={{ height: "260px", overflowY: "auto" }}>
      <Grid container spacing={2.5} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              bgcolor: "rgba(25, 118, 210, 0.05)",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <SettingsIcon color="primary" />
              {t("service_management")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "flex-start",
                width: "100%",
                gap: 4,
                flexWrap: "nowrap",
                px: 2,
              }}
            >
              <Box sx={{ minWidth: 250, maxWidth: 320 }}>
                {/* Email Service */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.emailServiceEnabled}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          emailServiceEnabled: e.target.checked,
                        }))
                      }
                      name="emailServiceEnabled"
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailIcon color="primary" />
                        <Typography variant="body1" fontWeight="medium">
                          {t("email_service")}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("email_service_description")}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start" }}
                />
              </Box>
              <Box sx={{ minWidth: 250, maxWidth: 320 }}>
                {/* Fee Payment Service */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.feePaymentService}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          feePaymentService: e.target.checked,
                        }))
                      }
                      name="feePaymentService"
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PaymentsIcon color="secondary" />
                        <Typography variant="body1" fontWeight="medium">
                          {t("fee_payment_service") || "Fee Payment Service"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("fee_payment_service_description") || "Enable online fee payment collection"}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start" }}
                />
              </Box>
            </Box>

          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep5 = () => {
    const defaultCenter: [number, number] =
      formData.geoLatitude && formData.geoLongitude
        ? [formData.geoLatitude, formData.geoLongitude]
        : [20.5937, 78.9629];

    const startTimeParts = parseTimeTo12Hour(formData.schoolStartTime || "09:00:00");
    const endTimeParts = parseTimeTo12Hour(formData.schoolEndTime || "17:00:00");

    return (
      <Box sx={{ height: "360px", overflowY: "auto", mt: 1, p: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "rgba(25, 118, 210, 0.02)" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.teacherAttendanceEnabled || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        teacherAttendanceEnabled: e.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label={
                  <Typography fontWeight="medium" variant="body2">
                    {t("Enable Teacher Attendance Module")}
                  </Typography>
                }
              />
            </Paper>
          </Grid>

          {formData.teacherAttendanceEnabled && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoLocateAddress}
                        onChange={(e) => setAutoLocateAddress(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        {t("Auto-locate typed address on map") || "Auto-locate typed address on map"}
                      </Typography>
                    }
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAutoFillAddress}
                    sx={{
                      fontSize: "0.75rem",
                      py: 0.5,
                      borderColor: "#3092e3",
                      color: "#3092e3",
                      "&:hover": {
                        borderColor: "#3092e3",
                        backgroundColor: "rgba(48, 146, 227, 0.08)",
                      },
                    }}
                  >
                    {t("Auto-fill from Details") || "Auto-fill from Details"}
                  </Button>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", mb: 1 }}>
                  <TextField
                    label={t("Search Address / Locate School")}
                    placeholder={t("e.g. 123 Main St, Jaipur, Rajasthan")}
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddressLookup(addressSearch);
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleAddressLookup(addressSearch)}
                    disabled={geocodeLoading}
                    sx={{ height: "40px" }}
                  >
                    {geocodeLoading ? <CircularProgress size={20} color="inherit" /> : t("Locate")}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("School Start Time")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={startTimeParts.hour}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(e.target.value as string, startTimeParts.minute, startTimeParts.period);
                        setFormData((prev) => ({ ...prev, schoolStartTime: newTime }));
                      }}
                    >
                      {hoursOptions.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={startTimeParts.minute}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(startTimeParts.hour, e.target.value as string, startTimeParts.period);
                        setFormData((prev) => ({ ...prev, schoolStartTime: newTime }));
                      }}
                    >
                      {minutesOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={startTimeParts.period}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(startTimeParts.hour, startTimeParts.minute, e.target.value as string);
                        setFormData((prev) => ({ ...prev, schoolStartTime: newTime }));
                      }}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("School End Time")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={endTimeParts.hour}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(e.target.value as string, endTimeParts.minute, endTimeParts.period);
                        setFormData((prev) => ({ ...prev, schoolEndTime: newTime }));
                      }}
                    >
                      {hoursOptions.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={endTimeParts.minute}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(endTimeParts.hour, e.target.value as string, endTimeParts.period);
                        setFormData((prev) => ({ ...prev, schoolEndTime: newTime }));
                      }}
                    >
                      {minutesOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={endTimeParts.period}
                      onChange={(e) => {
                        const newTime = formatTo24Hour(endTimeParts.hour, endTimeParts.minute, e.target.value as string);
                        setFormData((prev) => ({ ...prev, schoolEndTime: newTime }));
                      }}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography id="radius-slider" variant="body2" gutterBottom color="text.secondary">
                  {t("Geofence Radius (metres)")}: <strong>{formData.geoRadiusMetres || 200}m</strong>
                </Typography>
                <Slider
                  value={formData.geoRadiusMetres || 200}
                  onChange={(e, val) =>
                    setFormData((prev) => ({
                      ...prev,
                      geoRadiusMetres: val as number,
                    }))
                  }
                  min={50}
                  max={500}
                  step={50}
                  valueLabelDisplay="auto"
                  aria-labelledby="radius-slider"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" gutterBottom color="text.secondary" display="block">
                  {t("School Location (Click map or drag marker to set coordinates)")}
                </Typography>
                <Box
                  sx={{
                    height: "170px",
                    width: "100%",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #ccc",
                  }}
                >
                  <MapContainer
                    center={defaultCenter}
                    zoom={formData.geoLatitude ? 15 : 5}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <MapEvents />
                    <MapController center={defaultCenter} />
                    {formData.geoLatitude && formData.geoLongitude && (
                      <>
                        <Marker position={[formData.geoLatitude, formData.geoLongitude]} />
                        <Circle
                          center={[formData.geoLatitude, formData.geoLongitude]}
                          radius={formData.geoRadiusMetres || 200}
                          pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.15 }}
                        />
                      </>
                    )}
                  </MapContainer>
                </Box>
                <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("Latitude")}: <strong>{formData.geoLatitude?.toFixed(7) || t("Not Set")}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Longitude")}: <strong>{formData.geoLongitude?.toFixed(7) || t("Not Set")}</strong>
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  };

  function MapEvents() {
    useMapEvents({
      click(e) {
        setFormData((prev) => ({
          ...prev,
          geoLatitude: e.latlng.lat,
          geoLongitude: e.latlng.lng,
        }));
      },
    });
    return null;
  }

  function MapController({ center }: { center: [number, number] }) {
    const map = useMap();
    useEffect(() => {
      const currentZoom = map.getZoom();
      const targetZoom = currentZoom < 12 ? 15 : currentZoom;
      map.setView(center, targetZoom);
    }, [center, map]);
    return null;
  }

  const handleCloseWithDraft = () => {
    if (isLoading) return;
    if (!isEdit) {
      const hasData = formData.nameofSchool || formData.schoolCode || formData.diseCode ||
        formData.district || formData.state || formData.schoolPrincipalName ||
        formData.principalEmail ||
        (formData.medium && formData.medium.length > 0) ||
        (formData.schoolAdminIds && formData.schoolAdminIds.length > 0);
      if (hasData) {
        saveSchoolFormDraft(formData);
      }
    }
    handleClose();
  };

  const steps = [
    { label: t("general_details"), content: renderStep1() },
    { label: t("principal_details"), content: renderStep2() },
    { label: t("other_details"), content: renderStep3() },  // Changed from "services"
    { label: t("services"), content: renderStep4() },       // Changed from "additional_details"
    { label: t("attendance_settings") || "Attendance", content: renderStep5() }
  ];

  return (
    <CommonModal
      open={open}
      onClose={handleCloseWithDraft}
      title={isEdit ? t("edit_school") : t("add_new_school")}
      maxWidth="lg"
      disableEscapeKeyDown
      customActions={
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Box>
            {currentStep > 1 && (
              <Button
                onClick={handlePrevious}
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                disabled={isLoading}
                sx={{
                  minWidth: 100,
                  borderColor: "#666",
                  color: "#666",
                  "&:hover": { borderColor: "#333", color: "#333", backgroundColor: "rgba(102,102,102,0.08)" },
                }}
              >
                {t("previous")}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {currentStep < 5 && (
              <Button
                onClick={handleNext}
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                disabled={isLoading}
                sx={{
                  minWidth: 100,
                  borderColor: "#3092e3",
                  color: "#3092e3",
                  "&:hover": { borderColor: "#3092e3", color: "#3092e3", backgroundColor: "rgba(0,191,255,0.08)" },
                  "&.Mui-disabled": { borderColor: "#b2ebff", color: "#b2ebff", backgroundColor: "transparent", opacity: 1 },
                }}
              >
                {t("next")}
              </Button>
            )}
            {currentStep === 5 && (
              <Button
                onClick={() => handleSubmit(formData, imageFile || undefined, schoolImageFile || undefined)}
                variant="contained"
                disabled={isLoading}
                sx={{
                  minWidth: 100,
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "#3092e3" },
                  "&.Mui-disabled": { backgroundColor: "#b2ebff", color: "#fff" },
                }}
              >
                {isLoading ? (
                  <><CircularProgress size={20} sx={{ mr: 1 }} />{isCreating ? t("creating") : isUpdating ? t("updating") : t("processing")}</>
                ) : (
                  isEdit ? t("update") : t("create")
                )}
              </Button>
            )}
            <Button
              onClick={handleCloseWithDraft}
              variant="outlined"
              disabled={isLoading}
              sx={{
                minWidth: 100,
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
                "&.Mui-disabled": { borderColor: "#b2ebff", color: "#b2ebff", backgroundColor: "transparent", opacity: 1 },
              }}
            >
              {t("cancel")}
            </Button>
          </Box>
        </Box>
      }
    >
      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: 1,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="primary" sx={{ textAlign: 'center' }}>
            {isCreating ? t("creating_school") : isUpdating ? t("updating_school") : t("processing")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
            {t("please_wait")}
          </Typography>
        </Box>
      )}
      {/* Stepper */}
        <Box>
          <Stepper
            activeStep={currentStep - 1}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-root": {
                "& .MuiStepLabel-labelContainer": {
                  marginTop: "-7px",
                  marginBottom: "10px", // Add moderate space between circle and label
                },
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        {steps[currentStep - 1].content}

      {/* Logo Preview Modal */}
      <CommonModal
        open={logoPreviewModal}
        onClose={() => setLogoPreviewModal(false)}
        title={t('school_logo_preview')}
        maxWidth="sm"
        hideActions
        contentSx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, paddingTop: '24px !important' }}
      >
        {(imagePreview || authenticatedLogoUrl) && (
          <Box
            component="img"
            src={imagePreview || authenticatedLogoUrl || undefined}
            alt="School Logo Preview"
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          />
        )}
      </CommonModal>

      {/* School Image Preview Modal */}
      <CommonModal
        open={schoolImagePreviewModal}
        onClose={() => setSchoolImagePreviewModal(false)}
        title={t('school_image_preview')}
        maxWidth="md"
        hideActions
        contentSx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, paddingTop: '24px !important' }}
      >
        {(schoolImagePreview || authenticatedSchoolImageUrl) && (
          <Box
            component="img"
            src={schoolImagePreview || authenticatedSchoolImageUrl || undefined}
            alt="School Image Preview"
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          />
        )}
      </CommonModal>

      {/* User Form Modal for adding new school admin */}
      <UserFormModal
        open={userModalOpen}
        handleClose={() => setUserModalOpen(false)}
        onSubmit={(userData: UserData) => {
          createUserMutation.mutate(userData, {
            onSuccess: (data: UserData) => {
              setUserModalOpen(false);
              if (refetchSchoolAdmins) {
                refetchSchoolAdmins();
              }
              if (data && data.id) {
                setFormData(prev => ({
                  ...prev,
                  schoolAdminIds: [data.id as number]
                }));
                // Clear error when value is selected
                setErrors(prev => ({ ...prev, schoolAdminIds: null }));
              }
            },
            onError: (error: any) => {
              const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
              const errorLower = errorMessage.toLowerCase();

              // Check for duplicate email error - handle various formats
              if (errorLower.includes('email') &&
                (errorLower.includes('already') ||
                  errorLower.includes('exist') ||
                  errorLower.includes('duplicate') ||
                  errorLower.includes('taken'))) {
                showError('Email already used.');
              } else if (errorMessage) {
                showError(errorMessage);
              } else {
                showError(t('error_creating_user') || 'Failed to create user. Please try again.');
              }
            },
          });
        }}
        isEdit={false}
      />
    </CommonModal>
  );
};

export default SchoolFormModal;
